import {
  Bot,
  Brain,
  Cable,
  Camera,
  Container,
  Cpu,
  GitBranch,
  Globe,
  HardDrive,
  KeyRound,
  Layers,
  Timer,
} from 'lucide-react';
import {
  formatCustomSandboxSizeSpec,
  type PluginAttachmentValue,
  type SandboxOptions,
} from '@runloop/reflex-contract';
import {
  SANDBOX_RESOURCE_SIZE_OPTIONS,
  displayBlueprintName,
  getGitRepoAttachment,
} from '@reflex/shared';
import { DetailField } from '../shared/DetailList';
import { AgentTypeChip } from './AgentTypeChip';
import { ModelChip } from './ModelChip';
import { ResourceChip } from '../resource-preview/ResourceChip';
import { modelProviderSecretDisplayName } from '../model-providers/secret-display';
import { useAccessibleModelProviderSecrets } from '../../client/hooks';

/**
 * Normalized launch-configuration view shared by agents and personas. Both
 * store the same underlying config (model, repo via the `git-repo`
 * attachment, blueprint, pinned provider key, and sandbox options); callers
 * map their entity onto this shape. Fields that only one entity carries
 * (`modelEndpointId`, `reasoningEffort`) are optional and simply omitted by
 * callers that lack them.
 */
export interface AgentConfigDetails {
  agentType: string;
  model?: string | null;
  /** Persona-only: the pinned harness/model endpoint id. Agents omit this. */
  modelEndpointId?: string | null;
  /** Resolved reasoning effort, if the caller derived one. Agents omit this. */
  reasoningEffort?: string | null;
  modelProviderSecretId?: string | null;
  /** Carries the repo via the shared `git-repo` plugin attachment. */
  attachments?: PluginAttachmentValue[] | null;
  /** Set when the blueprint is resolved to a concrete id (agents). */
  blueprintId?: string | null;
  blueprintName?: string | null;
  sandboxOptions?: SandboxOptions | null;
}

function resourceSizeLabel(size: string): string {
  return SANDBOX_RESOURCE_SIZE_OPTIONS.find((o) => o.value === size)?.label ?? size;
}

/**
 * Read-only rows describing an agent/persona's launch configuration, rendered
 * as a fragment of {@link DetailField}s so the caller owns the surrounding
 * `DetailList` (and can interleave its own entity-specific rows such as owner,
 * timestamps, or an env-var summary). This is the single source of truth for
 * the agent detail panel and the persona detail page so the two read
 * identically.
 */
export function AgentConfigDetailFields({ config }: { config: AgentConfigDetails }) {
  const { secrets } = useAccessibleModelProviderSecrets({
    enabled: Boolean(config.modelProviderSecretId),
  });
  const providerSecret = config.modelProviderSecretId
    ? secrets.find((s) => s.id === config.modelProviderSecretId)
    : undefined;

  const repo = getGitRepoAttachment({ attachments: config.attachments });
  const blueprintName = config.sandboxOptions?.blueprintName ?? config.blueprintName ?? null;
  const sandbox = config.sandboxOptions;

  return (
    <>
      <DetailField icon={Cpu} label="Agent Type">
        <AgentTypeChip agentType={config.agentType} />
      </DetailField>
      {config.model ? (
        <DetailField icon={Bot} label="Model">
          <ModelChip model={config.model} />
        </DetailField>
      ) : null}
      {config.modelEndpointId ? (
        <DetailField icon={Cable} label="Harness" value={config.modelEndpointId} mono />
      ) : null}
      {config.reasoningEffort ? (
        <DetailField icon={Brain} label="Reasoning effort" value={config.reasoningEffort} />
      ) : null}
      {config.modelProviderSecretId ? (
        <DetailField icon={KeyRound} label="Provider key">
          <ResourceChip
            kind="cost-keys"
            id={config.modelProviderSecretId}
            name={providerSecret ? modelProviderSecretDisplayName(providerSecret) : undefined}
          />
        </DetailField>
      ) : null}
      <DetailField
        icon={Globe}
        label="Repository"
        value={repo?.repoSlug.replace('https://github.com/', '') ?? null}
        mono
      />
      <DetailField icon={GitBranch} label="Branch" value={repo?.repoBranch ?? null} mono />
      {config.blueprintId ? (
        <DetailField icon={Layers} label="Blueprint">
          <ResourceChip
            kind="blueprints"
            id={config.blueprintId}
            name={blueprintName ? displayBlueprintName(blueprintName) : undefined}
          />
        </DetailField>
      ) : blueprintName ? (
        <DetailField icon={Layers} label="Blueprint" value={displayBlueprintName(blueprintName)} />
      ) : null}
      {sandbox?.snapshotId ? (
        <DetailField icon={Camera} label="Snapshot" value={sandbox.snapshotId} mono />
      ) : null}
      {sandbox?.customSize ? (
        <DetailField
          icon={HardDrive}
          label="Devbox size"
          value={`${sandbox.customSize.name} · ${formatCustomSandboxSizeSpec(sandbox.customSize)}`}
        />
      ) : sandbox?.resourceSize ? (
        <DetailField
          icon={HardDrive}
          label="Devbox size"
          value={resourceSizeLabel(sandbox.resourceSize)}
        />
      ) : null}
      {sandbox?.dockerd ? (
        <DetailField icon={Container} label="Docker-in-Docker" value="Enabled" />
      ) : null}
      {sandbox?.idleTimeMinutes != null ? (
        <DetailField icon={Timer} label="Auto-suspend" value={`${sandbox.idleTimeMinutes} min`} />
      ) : null}
    </>
  );
}
